import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { openCalendly } from "./CalendlyWidget";

const stripHtml = (html = "") => String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

function cleanSlug(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9\-/]+/g, "-")
    .replace(/\/+/, "/")
    .replace(/^-+|-+$/g, "");
}

function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value || ""));
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function PublicationArticle() {
  const params = useParams();
  const slug = useMemo(() => cleanSlug(params.slug || ""), [params.slug]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`/api/publications.php?slug=${encodeURIComponent(slug)}`, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (_) {
          throw new Error(`Publication API returned non-JSON: ${text.slice(0, 120)}`);
        }
        if (!res.ok || !data.success || !data.page) {
          throw new Error(data.error || "Publication not found");
        }
        if (!cancelled) setPage(data.page);
      } catch (err) {
        if (!cancelled) setError(err.message || "Publication not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (slug) load();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!page) return;
    const canonical = page.canonical_url || `https://lionpridefinancial.com/${page.slug}/`;
    const title = page.title || page.h1 || "Lion Pride Financial Publication";
    const description = page.meta_description || stripHtml(page.excerpt || page.body || "").slice(0, 155);
    const image = page.og_image?.startsWith("http") ? page.og_image : `https://lionpridefinancial.com${page.og_image || "/logo.png"}`;
    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "article" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: image });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    setCanonical(canonical);
  }, [page]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-900 text-white">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 py-16">
        {loading && (
          <div className="rounded-2xl bg-white/10 border border-white/10 p-8 text-slate-200">
            Loading publication…
          </div>
        )}

        {!loading && error && (
          <section className="rounded-2xl bg-white/10 border border-white/10 p-8">
            <p className="text-cyan-300 text-sm uppercase tracking-widest font-semibold">Lion Pride Financial</p>
            <h1 className="text-3xl md:text-5xl font-bold mt-3">Publication not found</h1>
            <p className="text-slate-300 mt-4">{error}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/publications" className="bg-cyan-500 hover:bg-cyan-600 text-white px-5 py-3 rounded-lg font-semibold">
                View Publications
              </Link>
              <Link to="/" className="border border-white/20 hover:bg-white/10 text-white px-5 py-3 rounded-lg font-semibold">
                Back Home
              </Link>
            </div>
          </section>
        )}

        {!loading && page && (
          <article>
            <div className="rounded-3xl bg-white/10 border border-white/10 p-8 md:p-12 shadow-2xl">
              <p className="text-cyan-300 text-sm uppercase tracking-widest font-semibold">Lion Pride Financial Publication</p>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight mt-4">{page.h1 || page.title}</h1>
              {(page.excerpt || page.meta_description) && (
                <p className="text-xl text-slate-300 mt-6">{page.excerpt || page.meta_description}</p>
              )}
              {page.published_at && (
                <p className="text-sm text-slate-400 mt-6">Published {String(page.published_at).slice(0, 10)}</p>
              )}
            </div>

            <div
              className="publication-content mt-10 rounded-3xl bg-white/5 border border-white/10 p-7 md:p-10 text-slate-100 leading-8"
              dangerouslySetInnerHTML={{ __html: page.body || "" }}
            />

            <section className="mt-10 rounded-3xl bg-cyan-700 p-8 text-white">
              <h2 className="text-2xl font-bold">Ready to talk through the right strategy?</h2>
              <p className="mt-3 text-cyan-50">Use this as a starting point for a focused planning conversation.</p>
              {(!page.cta_url || page.cta_url === "/#contact" || page.cta_url === "#contact") ? (
                <button
                  type="button"
                  onClick={openCalendly}
                  className="inline-block mt-5 bg-white text-gray-900 px-5 py-3 rounded-lg font-semibold transition hover:bg-slate-100"
                >
                  {page.cta_label || "Book a Consultation"}
                </button>
              ) : (
                <a
                  href={page.cta_url}
                  className="inline-block mt-5 bg-white text-gray-900 px-5 py-3 rounded-lg font-semibold transition hover:bg-slate-100"
                >
                  {page.cta_label || "Book a Consultation"}
                </a>
              )}
            </section>

            <p className="mt-8 text-sm text-slate-400">
              Educational content only. Financial, tax, legal, and insurance strategies should be reviewed with properly licensed professionals.
            </p>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
